import React from 'react';

import {
  AnalyticsVisualizationLegendLocation as LegendLocation,
  AnalyticsVisualizationAggregationType as AggregationType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {
  AGGREGATION_TYPE_TO_READABLE,
  LEGEND_LOCATION_TO_READABLE,
} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {getColumnOptionDatas} from 'insights/analytics-visualizations/configs/config-components/ConfigEditorUtils';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {ORDERED_AGGREGATION_TYPES} from './config-components/AggregationConfigEditor';
import DropdownSelector, {StringValueDropdownSelector} from './form-components/DropdownSelector';
import TabbedConfigEditorSkeleton from './TabbedConfigEditorSkeleton';
import {VisualizationConfigEditorComponentProps} from './VisualizationConfigEditorTypes';

type PieChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationPieChartParametersNode'
>;

export type PieChartConfigEditorProps = VisualizationConfigEditorComponentProps<PieChartParameters>;

export default function PieChartConfigEditor(props: PieChartConfigEditorProps): JSX.Element {
  const {
    onUpdateParameters,
    columns,
    parameters,
    visualizationConfig,
    useSystemNames,
    enableAnalysisTab,
    enableFormatTab,
    enableAggregation,
    isDisabled = false,
  } = props;
  const hasCountAggregation = parameters.aggregationType === AggregationType.COUNT;

  return (
    <TabbedConfigEditorSkeleton
      setupTabContent={
        <>
          <div className="u-input-spacing-top">
            <FieldLabel label="Slice" />
            <DropdownSelector
              allowNull={false}
              onChange={(sliceColumnName: string) => {
                onUpdateParameters({sliceColumnName});
              }}
              searchable={true}
              selectedValue={parameters.sliceColumnName}
              optionDatas={getColumnOptionDatas(columns, useSystemNames)}
              disabled={isDisabled}
            />
          </div>
          {enableAggregation && (
            <div className="u-input-spacing-top">
              <FieldLabel label="Aggregation" />
              <StringValueDropdownSelector
                allowNull={false}
                onChange={(aggregationType) => {
                  onUpdateParameters({aggregationType});
                }}
                searchable={false}
                selectedValue={parameters.aggregationType}
                values={ORDERED_AGGREGATION_TYPES}
                valueToReadable={(aggregationType) => AGGREGATION_TYPE_TO_READABLE[aggregationType]}
                isDisabled={isDisabled}
              />
            </div>
          )}
          <div className="u-input-spacing-top">
            <FieldLabel label="Measure" />
            <DropdownSelector
              allowNull={hasCountAggregation}
              errorMessage={
                hasCountAggregation
                  ? 'Measure column can not be set when aggregation is set to "Count".'
                  : null
              }
              onChange={(measureColumnName: string) => {
                onUpdateParameters({measureColumnName});
              }}
              searchable={true}
              selectedValue={hasCountAggregation ? null : parameters.measureColumnName}
              optionDatas={getColumnOptionDatas(columns, useSystemNames)}
              disabled={isDisabled}
            />
          </div>
        </>
      }
      formatTabContent={
        <>
          <div className="u-input-spacing-top">
            <FieldLabel label="Legend location" />
            <StringValueDropdownSelector
              allowNull={true}
              onChange={(legendLocation) => {
                onUpdateParameters({legendLocation});
              }}
              searchable={false}
              selectedValue={parameters.legendLocation}
              values={[LegendLocation.LEFT, LegendLocation.RIGHT]}
              valueToReadable={(legendLocation) => LEGEND_LOCATION_TO_READABLE[legendLocation]}
              isDisabled={isDisabled}
            />
          </div>
          <div className="u-input-spacing-top">
            <FieldLabel label="Slice label" />
            <CheckboxInput
              id="show-slice-name"
              label="Slice name"
              className="u-mini-spacing-top"
              onChange={(e) => {
                onUpdateParameters({showSliceNameInLabel: e.target.checked});
              }}
              checked={parameters.showSliceNameInLabel}
              disabled={isDisabled}
            />
            <CheckboxInput
              id="show-percentage"
              label="Percentage"
              className="u-mini-spacing-top"
              onChange={(e) => {
                onUpdateParameters({showPercentageInLabel: e.target.checked});
              }}
              checked={parameters.showPercentageInLabel}
              disabled={isDisabled}
            />
            <CheckboxInput
              id="show-value"
              label="Value"
              className="u-mini-spacing-top"
              onChange={(e) => {
                onUpdateParameters({showValueInLabel: e.target.checked});
              }}
              checked={parameters.showValueInLabel}
              disabled={isDisabled}
            />
          </div>
        </>
      }
      enableAnalysisTab={enableAnalysisTab}
      enableFormatTab={enableFormatTab}
      visualizationConfigId={visualizationConfig.id}
      visualizationType={VisualizationType.PIE_CHART}
    />
  );
}
